import React from 'react';
import { PRODUCT_FAMILIES } from './ProductFamilyTabs';

/**
 * FamilyScopePicker — small inline filter shown above a cross-family-capable
 * admin selector (BOM component SKU dropdown, BOM Product dropdown, source
 * rate-card dropdown).
 *
 * Phase 2 (soft scoping): the active family tab still drives the DEFAULT
 * view, but admin can intentionally widen the scope to "All families" or
 * pick a specific family.
 *
 * Props:
 *  - value:    'current' | 'all' | 'vinyl' | 'cd' | 'dvd' | 'bluray'
 *  - onChange: (newScope) => void
 *  - activeFamily: the parent tab's active family (used to render the
 *                  "Active family ({label})" option label).
 *  - label:    optional label rendered above the picker (e.g. "Show items from")
 *  - className: optional wrapper class
 *  - size:     'sm' (default) | 'md'
 *
 * Scope-to-API-param resolution (consumer responsibility):
 *  - 'current'  → ?product_family={activeFamily}
 *  - 'all'      → ?product_family=all
 *  - <family>   → ?product_family=<family>
 */
export const SCOPE_VALUES = ['current', 'all', 'vinyl', 'cd', 'dvd', 'bluray'];

export function resolveScopeFamily(scope, activeFamily) {
  if (!scope || scope === 'current') return activeFamily;
  return scope; // 'all' | 'vinyl' | 'cd' | 'dvd' | 'bluray'
}

export default function FamilyScopePicker({
  value = 'current',
  onChange,
  activeFamily = 'vinyl',
  label = null,
  className = '',
  size = 'sm',
}) {
  const activeMeta = PRODUCT_FAMILIES.find((f) => f.id === activeFamily) || PRODUCT_FAMILIES[0];
  const baseClasses =
    size === 'sm'
      ? 'text-xs px-2 py-1'
      : 'text-sm px-2.5 py-1.5';

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="family-scope-picker">
      {label && <span className="text-xs text-slate-500 whitespace-nowrap">{label}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`border border-slate-300 rounded-md bg-white text-slate-700 ${baseClasses}`}
        data-testid="family-scope-picker-select"
      >
        <option value="current">Active family ({activeMeta.label})</option>
        <option value="all">All families</option>
        <option disabled>──────────</option>
        {PRODUCT_FAMILIES.map((fam) => (
          <option key={fam.id} value={fam.id}>
            {fam.label} only
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Inline family badge — render next to a SELECTED value that came from
 * another family than the active one. Always visible if the value's family
 * differs from the active family.
 */
export function FamilyBadge({ family, size = 'xs', className = '' }) {
  if (!family) return null;
  const meta = PRODUCT_FAMILIES.find((f) => f.id === family);
  const label = meta?.label || family;
  const sizeClasses = size === 'xs' ? 'text-[10px] px-1 py-0' : 'text-xs px-1.5 py-0.5';
  return (
    <span
      className={`inline-flex items-center ${sizeClasses} rounded bg-slate-100 text-slate-600 font-medium uppercase tracking-wide ${className}`}
      data-testid={`family-badge-${family}`}
    >
      {label}
    </span>
  );
}

/**
 * Cross-family warning chip — render next to a selected value when it does
 * NOT match the parent's active family. Amber to draw attention.
 */
export function CrossFamilyWarning({ valueFamily, activeFamily, className = '' }) {
  if (!valueFamily || !activeFamily || valueFamily === activeFamily) return null;
  const meta = PRODUCT_FAMILIES.find((f) => f.id === valueFamily);
  return (
    <span
      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium ${className}`}
      title={`This selection comes from another product family (${meta?.label || valueFamily}) — intentional cross-family link.`}
      data-testid="cross-family-warning"
    >
      ⚠ {meta?.label || valueFamily}
    </span>
  );
}
